import React, { useLayoutEffect, useRef } from "react";
import { FieldFeedbackLabel } from "./FieldFeedbackLabel";
import { caretAfterDigits, digitsOnly, formatCardNumber, normalizeCardNumber } from "../../../../helpers/paymentCard";

const getFieldCSSClasses = (touched, errors) => {
  const classes = ["form-control"];
  if (touched && errors) {
    classes.push("is-invalid");
  }
  return classes.join(" ");
};

/**
 * Shows a formatted value and keeps the caret next to the same digit it was after, instead of
 * letting React move it to the end when the new value is rendered.
 */
export function useFormattedInput() {
  const inputRef = useRef(null);
  const pendingCaret = useRef(null);

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (pendingCaret.current !== null && input && document.activeElement === input) {
      input.setSelectionRange(pendingCaret.current, pendingCaret.current);
    }
    pendingCaret.current = null;
  });

  const setValueAndCaret = (input, formatted, digitsBeforeCaret, caretAtEnd) => {
    const caret = caretAtEnd ? formatted.length : caretAfterDigits(formatted, digitsBeforeCaret);
    input.value = formatted;
    if (document.activeElement === input) {
      input.setSelectionRange(caret, caret);
      pendingCaret.current = caret;
    }
  };

  return { inputRef, setValueAndCaret };
}

/**
 * Card number input. Form state holds digits only (what the API receives); the input shows them
 * grouped with spaces for the detected brand. Always left-to-right, also in the Arabic interface.
 */
export function PaymentCardNumber({
  field,
  label,
  classes,
  customFeedbackLabel,
  form: { touched, errors, setFieldValue },
  withFeedbackLabel = true,
  ...props
}) {
  const { inputRef, setValueAndCaret } = useFormattedInput();

  const update = (input, digits, digitsBeforeCaret, caretAtEnd) => {
    const value = normalizeCardNumber(digits);
    setValueAndCaret(input, formatCardNumber(value), Math.min(digitsBeforeCaret, value.length), caretAtEnd);
    setFieldValue(field.name, value);
  };

  const handleChange = (e) => {
    const input = e.target;
    const raw = input.value;
    const caret = input.selectionStart == null ? raw.length : input.selectionStart;
    const previous = field.value || "";
    let digits = digitsOnly(raw);
    let digitsBeforeCaret = digitsOnly(raw.slice(0, caret)).length;

    // Only a space was deleted: delete the digit next to it, like the space was not there
    if (digits === previous && raw.length < formatCardNumber(previous).length) {
      const forward = e.nativeEvent && e.nativeEvent.inputType === "deleteContentForward";
      const index = forward ? digitsBeforeCaret : digitsBeforeCaret - 1;
      if (index >= 0 && index < digits.length) {
        digits = digits.slice(0, index) + digits.slice(index + 1);
        digitsBeforeCaret = index;
      }
    }

    update(input, digits, digitsBeforeCaret, caret === raw.length);
  };

  const selection = (input) => ({ start: input.selectionStart, end: input.selectionEnd });

  // Copy and cut give the digits only, so the number pastes cleanly anywhere
  const handleCopy = (e) => {
    const { start, end } = selection(e.target);
    if (start === end) return;
    e.preventDefault();
    e.clipboardData.setData("text/plain", digitsOnly(e.target.value.slice(start, end)));
  };

  const handleCut = (e) => {
    const input = e.target;
    const { start, end } = selection(input);
    if (start === end) return;
    handleCopy(e);
    const raw = input.value.slice(0, start) + input.value.slice(end);
    update(input, digitsOnly(raw), digitsOnly(raw.slice(0, start)).length, start === raw.length);
  };

  return (
    <>
      <input
        {...props}
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="cc-number"
        dir="ltr"
        className={`${classes} ${getFieldCSSClasses(touched[field.name], errors[field.name])} p-0 payment-card-ltr-input`}
        name={field.name}
        value={formatCardNumber(field.value)}
        onBlur={field.onBlur}
        onChange={handleChange}
        onCopy={handleCopy}
        onCut={handleCut}
      />
      {label && <label>{label}</label>}
      {withFeedbackLabel && (
        <FieldFeedbackLabel
          type="text"
          label={label}
          error={errors[field.name]}
          touched={touched[field.name]}
          customFeedbackLabel={customFeedbackLabel}
        />
      )}
    </>
  );
}
